import { type Dispatch } from 'react';
import { AccessibilityInfo } from 'react-native';

import { failToggleFields } from './state/loggerDraftHelpers';
import type { DraftSet, LoggerAction } from './state/loggerDraftTypes';
import { rowPrefix } from './timelineFlash';

// The two ✗ handlers, extracted from useTimelineLogger to keep that hook under
// the 500-line cap — same reason and same shape as its sibling
// useSetCountHandlers (LG-14 split). Behaviour-preserving: the code below is
// the code that lived inline, argument-for-argument.
//
// They are two different gestures wearing one button: onLogFail COMPLETES a
// pending set as failed, onToggleFail FLIPS an already-logged set's label.
export function useFailHandlers(args: {
  activeEntryIdx: number;
  boundSetIdx: number;
  boundSet: DraftSet | null;
  dispatch: Dispatch<LoggerAction>;
  /** The shared completion leg (rest transition + advance). `fail` lets the
   *  rest invite see a fail the raw cycle_set below has not dispatched yet. */
  commitSet: (loggedIdx: number, opts?: { fail?: boolean }) => { prFlash: boolean };
  /** LG-28 gate — see amrapGate.ts. */
  blockedOnReps: (setIdx: number) => boolean;
  maybePromptFailConfirm: (entryIdx: number, setIdx: number) => void;
  setFailFlash: (
    fn: (prev: { prefix: string; key: number } | null) => {
      prefix: string;
      key: number;
    },
  ) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const {
    activeEntryIdx,
    boundSetIdx,
    boundSet,
    dispatch,
    commitSet,
    blockedOnReps,
    maybePromptFailConfirm,
    setFailFlash,
    t,
  } = args;

  // Fail — log the bound set marked as failed (slice 5a). `fail` is reached via
  // the cycle_set state machine (pending → done → failed), not an update_set
  // patch, so we run the normal completion via cycleSetPill (pending → done:
  // PR check + rest timer) and then dispatch one more cycle_set to flip
  // done → failed. The reducer applies the two queued actions in order, so the
  // set lands completed-and-failed in a single tap; the fail leg clears prFlash
  // and keeps completedAt. A failed set still shows ✕ in the timeline and is
  // excluded from e1RM (computeE1rmKg). Mirrors onLogSet for superset advance.
  // Returns whether the set was actually COMPLETED (LG-69). Both refusals
  // below return false, and the caller needs to tell them apart from a real
  // commit: the empty-reps AMRAP refusal ends with a prompt asking the athlete
  // to TYPE, so a caller that dismissed the keyboard on every tap would close
  // it exactly when the app is asking for the keyboard.
  const onLogFail = (): boolean => {
    if (activeEntryIdx < 0 || boundSetIdx < 0) return false;
    const failedIdx = boundSetIdx;
    // ⚠️ commitSet refuses an empty-reps AMRAP silently, but the raw cycle_set
    // below would NOT: it would take the pending→done branch and complete the
    // set as an ORDINARY one, reps empty, which the save then drops without a
    // word. Gate before either (LG-28) — this leg forgot it once.
    if (blockedOnReps(failedIdx)) return false;
    commitSet(failedIdx, { fail: true });
    dispatch({ type: 'cycle_set', entryIdx: activeEntryIdx, setIdx: boundSetIdx });
    // Confirm the fail rep count (AP-0p) — before the focus advance below,
    // so it targets the just-failed set regardless of any superset hop.
    maybePromptFailConfirm(activeEntryIdx, failedIdx);
    // Flash the just-failed row in the timeline (both the superset and the
    // standalone branch below advance away from it), so the log is noticeable
    // without the old banner.
    setFailFlash((prev) => ({
      prefix: rowPrefix(activeEntryIdx, failedIdx),
      key: (prev?.key ?? 0) + 1,
    }));
    // Screen-reader signal: the removed fail cue was `live`; the ordinary
    // advance cue is deliberately quiet, and the rep-confirm modal only opens
    // for rep-based sets — so announce the fail here so cardio/timed and
    // no-rep-count fails aren't silent for assistive tech. No-op when no screen
    // reader is active (sighted users never hear it).
    AccessibilityInfo.announceForAccessibility(
      t('mobile.logger.failBadgeA11y', { defaultValue: 'failed set' }),
    );
    // The advance itself was already armed by commitSet above: a fail takes the
    // SAME transition as a successful log, rest included (Antenore 2026-07-20 —
    // a fail is a normal training outcome, not an error; it must not stay put
    // or raise a banner). The just-failed set stays a row in the timeline
    // (marked "Failed"); correct or undo it there like any other set. The
    // FailConfirmModal above is the mis-tap escape (cancel = "not a fail" →
    // clears the fail, keeps the reps).
    return true;
  };

  // Fail-toggle on an ALREADY-completed set (edit-scope slice 5b). The same ✗
  // button, but on a logged set it FLIPS done↔failed in place instead of
  // committing a pending set. Routed through update_set (a value edit) — NOT
  // cycleSetPill/cycle_set: cycle_set's failed→pending leg would un-LOG the set
  // (wipe completedAt), and we must never re-time or un-complete on an edit
  // (timeline integrity, next/timeline.md). Clears prFlash so a former-PR set
  // doesn't render trophy + ✕ together (timelineRows reads set.prFlash); the
  // record is recomputed server-side at save regardless. No superset advance
  // and no selection reset — the athlete stays on the set to see the flip / undo.
  const onToggleFail = () => {
    if (activeEntryIdx < 0 || boundSetIdx < 0 || !boundSet) return;
    if (boundSet.completedAt === null) return; // pending → use onLogSet/onLogFail
    const becomingFailed = !boundSet.fail;
    // Editing an ALREADY-completed set: `fail` is a pure label (AP-0p model),
    // so this just flips it — NO rep decrement + NO confirm popup. The reps
    // are already the clean count here (the set was logged); marking it failed
    // means "I also failed one beyond", not "one of these counted reps broke".
    // The decrement/confirm only happens on the pending→failed path (onLogFail,
    // where the entered reps include the attempt that broke). Adjust reps in
    // the row if needed.
    dispatch({
      type: 'update_set',
      entryIdx: activeEntryIdx,
      setIdx: boundSetIdx,
      patch: failToggleFields(becomingFailed),
    });
  };

  return { onLogFail, onToggleFail };
}
